// LoopEvent reducer extracted from sessionStore.ts (docs/plans/13
// §M3). Returns a partial state slice the zustand setter merges in.

import type { LoopEvent, ToolCallEvent, ToolResultEvent } from "@/lib/events";
import { pickArtifactsFromTool } from "@/stores/sessionStore.artifacts";
import {
  newId,
  type ChatMessage,
  type SessionState,
  type ToolActivity,
} from "@/stores/sessionStore.types";

export function applyToolCall(state: SessionState, event: ToolCallEvent): ToolActivity[] {
  const activity: ToolActivity = {
    id: event.tool_call_id,
    toolName: event.tool_name,
    status: "running",
    arguments: event.arguments,
    startedAt: event.timestamp,
  };
  return [...state.tools, activity];
}

export function applyToolResult(state: SessionState, event: ToolResultEvent): ToolActivity[] {
  return state.tools.map((t) =>
    t.id === event.tool_call_id
      ? {
          ...t,
          status: "ok",
          result: event.result,
          completedAt: event.timestamp,
          durationMs: event.duration_ms,
        }
      : t,
  );
}

function pushMessage(messages: ChatMessage[], message: ChatMessage): ChatMessage[] {
  return [...messages, message];
}

export function applyEvent(state: SessionState, event: LoopEvent): Partial<SessionState> {
  switch (event.type) {
    case "user_message":
      return {
        messages: pushMessage(state.messages, {
          id: newId(),
          role: "user",
          content: event.content,
          timestamp: event.timestamp,
        }),
      };
    case "assistant_text":
      return {
        messages: pushMessage(state.messages, {
          id: newId(),
          role: "assistant",
          content: event.content,
          timestamp: event.timestamp,
        }),
      };
    case "tool_call":
      return { tools: applyToolCall(state, event) };
    case "tool_result": {
      const tools = applyToolResult(state, event);
      // Look up the original tool_call's arguments so artifact
      // capture can use the query / params, not just the result.
      const matched = state.tools.find((t) => t.id === event.tool_call_id);
      const args = matched?.arguments ?? {};
      const artifacts = pickArtifactsFromTool(
        state.artifacts,
        event.tool_name,
        event.result,
        args,
      );
      return { tools, artifacts };
    }
    case "tool_deny":
      // Permission-gate denies are agent-internal control signals,
      // not user-facing chat. The Specialists tab surfaces the
      // blocked tool + reason; the agent will retry with the
      // suggested specialist on the next turn. Pasting the raw
      // reason text in chat just confuses the user with
      // implementation jargon ("AgentTool with type='...'", etc.).
      return {
        tools: state.tools.map((t) =>
          t.id === event.tool_call_id
            ? {
                ...t,
                status: "denied",
                reason: event.reason,
                completedAt: event.timestamp,
              }
            : t,
        ),
      };
    case "tool_error":
      return {
        tools: state.tools.map((t) =>
          t.id === event.tool_call_id
            ? {
                ...t,
                status: "error",
                errorType: event.error_type,
                reason: event.error,
                completedAt: event.timestamp,
              }
            : t,
        ),
      };
    case "turn_complete":
      return { pendingTurn: false };
    case "recovery_retry":
      return {
        messages: pushMessage(state.messages, {
          id: newId(),
          role: "system",
          content: `Recovering: ${event.reason} (retrying in ${event.wait_seconds.toFixed(1)}s)`,
          timestamp: event.timestamp,
        }),
      };
    case "error":
      return {
        messages: pushMessage(state.messages, {
          id: newId(),
          role: "system",
          content: `Server error: ${event.error}`,
          timestamp: new Date().toISOString(),
        }),
      };
    default:
      return {};
  }
}
